import React from "react";
import { Mail, Phone, Calendar, Sparkles, Loader2, BarChart3, TrendingUp, TrendingDown, Award, Building2 } from "lucide-react";
import { UserAvatar, StatusBadge, Separator } from "../ui";

/**
 * StudentProfileCard — Left sidebar card with gradient banner, avatar, name, contact info, AI Report.
 */
const StudentProfileCard = ({ student, className = "", readOnly, latestReport, generatingReport, onGenerateReport, onViewHistory, onViewReport }) => {
  const joinedDate = student.createdAt
    ? new Date(student.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Kolkata" })
    : "—";

  const reportDate = latestReport?.generatedAt || latestReport?.createdAt;
  const score = latestReport?.overallScore ?? 0;
  const scoreColor = score >= 75 ? '#10b981' : score >= 50 ? '#f59e0b' : '#ef4444';
  const scoreBg = score >= 75 ? 'rgba(16,185,129,0.08)' : score >= 50 ? 'rgba(245,158,11,0.08)' : 'rgba(239,68,68,0.08)';
  const gradeBg = score >= 75 ? '#ecfdf5' : score >= 50 ? '#fffbeb' : '#fef2f2';
  const gradeColor = score >= 75 ? '#065f46' : score >= 50 ? '#92400e' : '#991b1b';

  return (
    <div className={`sp-profile-card ${className}`}>
      {/* Gradient banner with mesh effect */}
      <div className="sp-profile-banner">
        <div className="sp-profile-banner__mesh" />
      </div>

      <div className="sp-profile-body">
        {/* Avatar — supports profile photo */}
        <div className="sp-profile-avatar-wrap">
          <UserAvatar
            name={student.name}
            src={student.profilePhoto || undefined}
            size="xl"
            bordered
          />
          {student.status === 'active' && <span className="sp-profile-avatar-dot" />}
        </div>

        <h2 className="sp-profile-name">{student.name}</h2>

        <div className="sp-profile-badges">
          <StatusBadge status={student.status || "active"} />
          {student.course?.name && (
            <StatusBadge status="outline">{student.course.name}</StatusBadge>
          )}
        </div>

        <Separator className="sp-profile-sep" />

        <div className="sp-profile-contact">
          <div className="sp-profile-contact-row">
            <div className="sp-profile-contact-icon-wrap">
              <Mail size={14} />
            </div>
            <span className="sp-profile-contact-text" title={student.email}>
              {student.email || "—"}
            </span>
          </div>
          <div className="sp-profile-contact-row">
            <div className="sp-profile-contact-icon-wrap">
              <Phone size={14} />
            </div>
            <span className="sp-profile-contact-text">{student.mobile || "—"}</span>
          </div>
          <div className="sp-profile-contact-row">
            <div className="sp-profile-contact-icon-wrap">
              <Calendar size={14} />
            </div>
            <span className="sp-profile-contact-text">Joined {joinedDate}</span>
          </div>
          {student.batch?.branchId?.name && (
            <div className="sp-profile-contact-row">
              <div className="sp-profile-contact-icon-wrap">
                <Building2 size={14} />
              </div>
              <span className="sp-profile-contact-text">{student.batch.branchId.name}</span>
            </div>
          )}
        </div>

        {readOnly && (
          <>
            <Separator className="sp-profile-sep" />
            <div className="sp-profile-ai-section">
              {/* AI Report Score Card */}
              {latestReport ? (
                <div
                  className="sp-report-card"
                  onClick={() => onViewReport?.(latestReport._id)}
                  role="button"
                  tabIndex={0}
                  style={{ '--score-color': scoreColor, '--score-bg': scoreBg }}
                >
                  <div className="sp-report-card__header">
                    <Award size={14} style={{ color: scoreColor }} />
                    <span className="sp-report-card__title">AI Performance</span>
                    <span className="sp-report-card__date">
                      {reportDate ? new Date(reportDate).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : ''}
                    </span>
                  </div>
                  <div className="sp-report-card__body">
                    <div className="sp-report-card__score-ring">
                      <svg viewBox="0 0 36 36" className="sp-report-card__ring-svg">
                        <path
                          d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          fill="none"
                          stroke="var(--border-secondary, #e2e8f0)"
                          strokeWidth="3"
                        />
                        <path
                          d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          fill="none"
                          stroke={scoreColor}
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeDasharray={`${score}, 100`}
                        />
                      </svg>
                      <span className="sp-report-card__score-text" style={{ color: scoreColor }}>
                        {score}%
                      </span>
                    </div>
                    <div className="sp-report-card__details">
                      <span
                        className="sp-report-card__grade-badge"
                        style={{ background: gradeBg, color: gradeColor }}
                      >
                        Grade {latestReport.grade}
                      </span>
                      <span className="sp-report-card__trend">
                        {score >= 50
                          ? <><TrendingUp size={12} style={{ color: '#10b981' }} /> On Track</>
                          : <><TrendingDown size={12} style={{ color: '#ef4444' }} /> Needs Focus</>
                        }
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="sp-report-card sp-report-card--empty">
                  <Award size={20} className="sp-report-card__empty-icon" />
                  <p className="sp-report-card__empty-text">No AI Report generated yet</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="sp-report-actions">
                <button
                  className="sp-report-actions__generate"
                  onClick={onGenerateReport}
                  disabled={generatingReport}
                >
                  {generatingReport ? (
                    <><Loader2 size={15} className="spin-animation" /> Generating...</>
                  ) : (
                    <><Sparkles size={15} /> Generate Report</>
                  )}
                </button>
                {latestReport && (
                  <button
                    className="sp-report-actions__history"
                    onClick={onViewHistory}
                  >
                    <BarChart3 size={14} />
                    History
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default StudentProfileCard;
